// =========================================
// Evaluation Types
// =========================================

import type { CriterionType } from '@/features/criteria/types';

// AI model options
export type AIModel = 'gpt-4-turbo' | 'claude-3.5-sonnet' | 'gpt-4o-mini' | 'gpt-4.1-mini';

export const AI_MODEL_LABELS: Record<AIModel, string> = {
  'gpt-4-turbo': 'GPT-4 Turbo',
  'claude-3.5-sonnet': 'Claude 3.5 Sonnet',
  'gpt-4o-mini': 'GPT-4o Mini',
  'gpt-4.1-mini': 'GPT-4.1 Mini',
};

// =========================================
// Core evaluation types
// =========================================

export type EvaluationStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface Evaluation {
  id: string;
  user_id: string;
  source_id: string;
  status: EvaluationStatus;
  score: number | null;
  breakdown: EvaluationBreakdown | null;
  ai_model: AIModel;
  credits_used: number;
  cited_passages: CitedPassage[];
  created_at: string;
  completed_at: string | null;
  sources?: { id: string; file_name: string; title: string | null } | null;
}

export interface EvaluationBreakdown {
  total_score: number;
  max_possible: number;
  percentage: number;
  criteria_count: number;
  weighted_scores: WeightedScore[];
}

export interface WeightedScore {
  criteria_id: string;
  criteria_name: string;
  criteria_type: CriterionType;
  raw_score: number;
  weight: number;
  weighted_score: number;
  max_weighted: number;
}

// =========================================
// Evaluation criteria results
// =========================================

export interface EvaluationCriterion {
  id: string;
  evaluation_id: string;
  criteria_id: string;
  result: CriterionResult;
  score: number | null;
  explanation: string | null;
  passages: CitedPassage[];
}

// Result shapes per criterion type
export interface BooleanResult {
  answer: boolean;
  confidence: number; // 0-1
}

export interface ScaleResult {
  value: number;
  min: number;
  max: number;
  confidence?: number; // 0-1
}

export interface TextMatchResult {
  matched_keywords: string[];
  total_keywords: number;
  match_ratio: number;
  confidence?: number; // 0-1
}

export interface PICOResult {
  population: { score: number; found: boolean; detail: string };
  intervention: { score: number; found: boolean; detail: string };
  comparator: { score: number; found: boolean; detail: string };
  outcome: { score: number; found: boolean; detail: string };
  confidence?: number; // 0-1
}

export interface BiasRiskResult {
  items: Array<{
    label: string;
    rating: string;
    justification: string;
  }>;
  overall_risk: string;
  confidence?: number; // 0-1
}

// =========================================
// Passage validation
// =========================================

export type PassageValidationStatus = 'verified' | 'unverified' | 'not_found';

export interface PassageValidation {
  passage_text: string;
  status: PassageValidationStatus;
  similarity: number;
  criteria_id: string | null;
}

export type CriterionResult =
  | BooleanResult
  | ScaleResult
  | TextMatchResult
  | PICOResult
  | BiasRiskResult;

// =========================================
// Passage citations
// =========================================

export interface CitedPassage {
  text: string;
  page: number | null;
  start_offset: number | null;
  end_offset: number | null;
  criteria_id: string | null;
  relevance: string; // brief explanation of why this passage is cited
}

// =========================================
// Evaluation with relations (joined data)
// =========================================

export interface EvaluationWithDetails extends Evaluation {
  source?: {
    id: string;
    file_name: string;
    title: string | null;
    authors: string[] | null;
  };
  criteria_results?: EvaluationCriterion[];
}

// =========================================
// Input types
// =========================================

export interface RunEvaluationInput {
  source_ids: string[];
  criteria_ids: string[];
  ai_model: AIModel;
}

export interface BatchEvaluationProgress {
  total: number;
  completed: number;
  failed: number;
  current_source_id: string | null;
  results: EvaluationWithDetails[];
}

// =========================================
// Screening mode (batch 50x cheaper)
// =========================================

export type EvaluationMode = 'full' | 'screening';
export const SCREENING_BATCH_SIZE = 50;
export const SCREENING_CREDITS_PER_BATCH = 1;

export interface ScreeningVerdict {
  source_id: string;
  verdict: BucketCategory;
  reason: string;
  score: number;
}

export interface ScreeningProgress {
  total_sources: number;
  total_batches: number;
  completed_batches: number;
  completed_sources: number;
  failed_batches: number;
  results: ScreeningVerdict[];
}

// =========================================
// Auto-bucketing
// =========================================

export type BucketCategory = 'include' | 'maybe' | 'exclude';

export interface BucketConfig {
  include_threshold: number; // score >= this → include (default 70)
  exclude_threshold: number; // score < this → exclude (default 40)
}

export const DEFAULT_BUCKET_CONFIG: BucketConfig = {
  include_threshold: 70,
  exclude_threshold: 40,
};

export interface BucketResult {
  category: BucketCategory;
  source_id: string;
  score: number;
  folder_id: string | null; // assigned result folder
}

export function getBucketCategory(score: number, config: BucketConfig = DEFAULT_BUCKET_CONFIG): BucketCategory {
  if (score >= config.include_threshold) return 'include';
  if (score < config.exclude_threshold) return 'exclude';
  return 'maybe';
}

export const BUCKET_COLORS: Record<BucketCategory, string> = {
  include: 'emerald',
  maybe: 'amber',
  exclude: 'rose',
};

export const BUCKET_LABELS: Record<BucketCategory, string> = {
  include: 'Include',
  maybe: 'Maybe',
  exclude: 'Exclude',
};
